import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Calendar,
  Edit,
  XCircle,
  RotateCcw,
  DollarSign,
  Package,
  User,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  FileSpreadsheet,
} from "lucide-react";

export default function AdminVentas() {
  const [data, setData] = useState({ ventas: [], total: 0 });
  const [catalogOrders, setCatalogOrders] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showAnuladas, setShowAnuladas] = useState(true);
  const [showCatalogo, setShowCatalogo] = useState(true);
  
  // Filters
  const [filtroVendedor, setFiltroVendedor] = useState("todos");
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [search, setSearch] = useState("");
  
  // Dialogs
  const [editDialog, setEditDialog] = useState({ open: false, venta: null });
  const [anularDialog, setAnularDialog] = useState({ open: false, venta: null, motivo: "" });
  const [exporting, setExporting] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    metodo_pago: "efectivo",
    notas: "",
    notas_edicion: "",
    items: []
  });

  useEffect(() => {
    fetchVendedores();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filtroVendedor, filtroFechaInicio, filtroFechaFin, showAnuladas, showCatalogo]);

  const fetchVendedores = async () => {
    try {
      const res = await api.get("/usuarios?rol=vendedor");
      setVendedores(res.data);
    } catch (error) {
      console.error("Error fetching vendedores:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { incluir_anuladas: showAnuladas };
      if (filtroVendedor !== "todos") params.vendedor_id = filtroVendedor;
      if (filtroFechaInicio) params.fecha_inicio = filtroFechaInicio;
      if (filtroFechaFin) params.fecha_fin = filtroFechaFin;

      // Fetch ventas directas
      const ventasRes = await api.get("/ventas/admin/todas", { params });
      setData(ventasRes.data);
      
      // Fetch catalog orders
      if (showCatalogo) {
        const catalogParams = {};
        if (filtroVendedor !== "todos") catalogParams.vendedor_id = filtroVendedor;
        if (filtroFechaInicio) catalogParams.fecha_inicio = filtroFechaInicio;
        if (filtroFechaFin) catalogParams.fecha_fin = filtroFechaFin;
        
        const catalogRes = await api.get("/catalogo/admin/orders", { params: catalogParams });
        // API returns { pedidos: [...], total: X, total_monto: X }
        const pedidos = catalogRes.data?.pedidos || catalogRes.data || [];
        // Transform catalog orders to match ventas format
        const transformedOrders = pedidos.map(order => ({
          ...order,
          fecha: order.fecha_creacion,
          vendedor_nombre: order.vendedor_nombre,
          origen: "catalogo_digital",
          anulada: order.estado === "cancelado",
          editada: false,
          // Map catalog payment methods to display
          metodo_pago_display: order.metodo_pago === "transferencia_baruj" ? "Transfer. Baruj" :
                              order.metodo_pago === "transferencia_vendedor" ? "Transfer. Vendedor" :
                              order.metodo_pago === "contra_entrega" ? "Contra Entrega" :
                              order.metodo_pago === "efectivo_vendedor" ? "Efectivo" : order.metodo_pago
        }));
        setCatalogOrders(transformedOrders);
      } else {
        setCatalogOrders([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar las ventas");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (venta) => {
    setEditForm({
      cliente_nombre: venta.cliente_nombre || "",
      cliente_telefono: venta.cliente_telefono || "",
      metodo_pago: venta.metodo_pago || "efectivo",
      notas: venta.notas || "",
      notas_edicion: "",
      items: venta.items || []
    });
    setEditDialog({ open: true, venta });
  };

  const handleEdit = async () => {
    try {
      await api.put(
        `/ventas/${editDialog.venta.id}?notas_edicion=${encodeURIComponent(editForm.notas_edicion || "Edición administrativa")}`,
        {
          cliente_nombre: editForm.cliente_nombre,
          cliente_telefono: editForm.cliente_telefono,
          metodo_pago: editForm.metodo_pago,
          notas: editForm.notas,
          items: editForm.items
        }
      );
      toast.success("Venta actualizada correctamente");
      setEditDialog({ open: false, venta: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar la venta");
    }
  };

  const handleAnular = async () => {
    if (!anularDialog.motivo || anularDialog.motivo.length < 5) {
      toast.error("El motivo debe tener al menos 5 caracteres");
      return;
    }
    try {
      await api.post(`/ventas/${anularDialog.venta.id}/anular?motivo=${encodeURIComponent(anularDialog.motivo)}`);
      toast.success("Venta anulada - No se incluirá en comisiones");
      setAnularDialog({ open: false, venta: null, motivo: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al anular");
    }
  };

  const handleRestaurar = async (venta) => {
    try {
      await api.post(`/ventas/${venta.id}/restaurar`);
      toast.success("Venta restaurada correctamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar");
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    // Recalculate subtotal
    if (field === "cantidad" || field === "precio_unitario") {
      newItems[index].subtotal = (newItems[index].cantidad || 0) * (newItems[index].precio_unitario || 0);
    }
    setEditForm({ ...editForm, items: newItems });
  };

  const removeItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, items: newItems });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Bogota"
    });
  };

  // Export to Excel (CSV)
  const exportToExcel = () => {
    setExporting(true);
    try {
      // Get all vendedores info for zone lookup
      const vendedorMap = {};
      vendedores.forEach(v => {
        vendedorMap[v.id] = v;
      });

      // Headers - updated to include catalog orders
      const headers = [
        "ID Venta",
        "Tipo",
        "Fecha",
        "Hora",
        "Vendedor",
        "Zona",
        "Tipo Vendedor",
        "Teléfono Vendedor",
        "Producto",
        "Cantidad",
        "Precio Unitario",
        "Subtotal",
        "Total Venta",
        "Método de Pago",
        "Estado",
        "Cliente Nombre",
        "Cliente Teléfono",
        "Dirección",
        "Barrio",
        "Notas",
        "Motivo Anulación"
      ];

      // Build rows - one per item
      const rows = [];
      
      // Add ventas directas
      filtered.forEach(venta => {
        if (venta.origen === "catalogo_digital") return; // Skip catalog orders here, we'll add them separately
        
        const vendedor = vendedorMap[venta.vendedor_id] || {};
        const fechaStr = venta.fecha || "";
        const fecha = fechaStr.slice(0, 10);
        const hora = fechaStr.slice(11, 16);
        const estado = venta.anulada ? "Anulada" : venta.editada ? "Editada" : "Activa";

        (venta.items || []).forEach(item => {
          rows.push([
            venta.id,
            "Venta Directa",
            fecha,
            hora,
            venta.vendedor_nombre || "",
            vendedor.zona || "Sin zona",
            vendedor.tipo_vendedor || "",
            vendedor.telefono || "",
            item.producto_nombre || "",
            item.cantidad || 0,
            item.precio_unitario || 0,
            item.subtotal || 0,
            venta.total || 0,
            venta.metodo_pago || "efectivo",
            estado,
            venta.cliente_nombre || "",
            venta.cliente_telefono || "",
            "",
            "",
            (venta.notas || "").replace(/,/g, ";").replace(/\n/g, " "),
            (venta.motivo_anulacion || "").replace(/,/g, ";").replace(/\n/g, " ")
          ]);
        });
      });
      
      // Add catalog orders
      filtered.forEach(order => {
        if (order.origen !== "catalogo_digital") return; // Skip regular ventas
        
        const vendedor = vendedorMap[order.vendedor_id] || {};
        const fechaStr = order.fecha || order.fecha_creacion || "";
        const fecha = fechaStr.slice(0, 10);
        const hora = fechaStr.slice(11, 16);
        const estado = order.estado === "cancelado" ? "Cancelado" : 
                      order.estado === "entregado" ? "Entregado" : 
                      order.estado === "confirmado" ? "Confirmado" : order.estado;

        (order.items || []).forEach(item => {
          rows.push([
            order.numero_pedido || order.id,
            "Catálogo Digital",
            fecha,
            hora,
            order.vendedor_nombre || "",
            vendedor.zona || "Sin zona",
            vendedor.tipo_vendedor || "",
            vendedor.telefono || "",
            item.producto_nombre || "",
            item.cantidad || 0,
            item.precio_unitario || 0,
            item.subtotal || 0,
            order.total || 0,
            order.metodo_pago_display || order.metodo_pago || "",
            estado,
            order.cliente_nombre || "",
            order.cliente_telefono || "",
            (order.direccion_entrega || "").replace(/,/g, ";"),
            (order.barrio_entrega || "").replace(/,/g, ";"),
            (order.notas || "").replace(/,/g, ";").replace(/\n/g, " "),
            ""
          ]);
        });
      });

      // Convert to CSV
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Create and download file
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fechaHoy = new Date().toLocaleDateString("es-CO").replace(/\//g, "-");
      link.download = `ventas_completas_${fechaHoy}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exportados ${rows.length} registros a Excel`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  // Combine ventas and catalog orders for display
  const allTransactions = [
    ...(data.ventas || []),
    ...catalogOrders
  ].sort((a, b) => {
    const dateA = new Date(a.fecha || a.fecha_creacion);
    const dateB = new Date(b.fecha || b.fecha_creacion);
    return dateB - dateA; // Most recent first
  });

  const filtered = allTransactions.filter(
    (v) =>
      v.vendedor_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      v.cliente_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      v.id?.includes(search) ||
      v.numero_pedido?.includes(search)
  );
  
  // Calculate totals including catalog orders
  const totalCatalogo = catalogOrders
    .filter(o => o.estado !== "cancelado")
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const cantidadCatalogo = catalogOrders.filter(o => o.estado !== "cancelado").length;

  const totalEditForm = editForm.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);

  if (loading && !data.ventas?.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ventas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Gestión de Ventas</h1>
          <p className="text-torta-muted">Editar, anular o restaurar ventas registradas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button 
            variant="outline" 
            onClick={exportToExcel} 
            disabled={exporting || filtered.length === 0}
            className="border-green-300 text-green-600 hover:bg-green-50"
          >
            <FileSpreadsheet className={`w-4 h-4 mr-2 ${exporting ? "animate-spin" : ""}`} />
            Descargar Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-torta-brown to-[#A0522D] rounded-2xl p-5 text-white">
          <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-white/80 text-sm">Ventas Directas</p>
          <p className="font-display text-2xl">{formatCurrency(data.total_activas)}</p>
          <p className="text-xs text-white/60">{data.cantidad_activas || 0} transacciones</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-2xl p-5 text-white">
          <Package className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-white/80 text-sm">Catálogo Digital</p>
          <p className="font-display text-2xl">{formatCurrency(totalCatalogo)}</p>
          <p className="text-xs text-white/60">{cantidadCatalogo} pedidos</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <TrendingDown className="w-8 h-8 text-red-500 mb-2" />
          <p className="text-red-600 text-sm">Ventas Anuladas</p>
          <p className="font-display text-2xl text-red-700">{formatCurrency(data.total_anuladas)}</p>
          <p className="text-xs text-red-500">{data.cantidad_anuladas || 0} transacciones</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <DollarSign className="w-8 h-8 text-blue-500 mb-2" />
          <p className="text-blue-600 text-sm">Total Combinado</p>
          <p className="font-display text-2xl text-blue-700">{formatCurrency((data.total_activas || 0) + totalCatalogo)}</p>
          <p className="text-xs text-blue-500">{(data.cantidad_activas || 0) + cantidadCatalogo} registros</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-2" />
          <p className="text-amber-600 text-sm">Importante</p>
          <p className="text-sm text-amber-800">Las ventas anuladas no afectan el cálculo de comisiones</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-torta-dark font-medium">
          <Filter className="w-4 h-4" /> Filtros
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-torta-muted" />
            <Input
              placeholder="Buscar por vendedor, cliente o ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filtroVendedor} onValueChange={setFiltroVendedor}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Vendedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los vendedores</SelectItem>
              {vendedores.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={filtroFechaInicio}
            onChange={(e) => setFiltroFechaInicio(e.target.value)}
            className="w-40"
            placeholder="Desde"
          />
          <Input
            type="date"
            value={filtroFechaFin}
            onChange={(e) => setFiltroFechaFin(e.target.value)}
            className="w-40"
            placeholder="Hasta"
          />
          <div className="flex items-center gap-2">
            <Switch
              checked={showAnuladas}
              onCheckedChange={setShowAnuladas}
            />
            <span className="text-sm text-torta-muted">Mostrar anuladas</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={showCatalogo}
              onCheckedChange={setShowCatalogo}
            />
            <span className="text-sm text-torta-muted">Incluir Catálogo Digital</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-torta-sand/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Fecha</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Vendedor</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Cliente</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Productos</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Estado</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Total</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-torta-sand/50">
              {filtered.map((venta) => {
                const isExpanded = expandedId === venta.id;
                const unidades = (venta.items || []).reduce((sum, i) => sum + (i.cantidad || 0), 0);
                const isCatalogOrder = venta.origen === "catalogo_digital";
                
                return (
                  <React.Fragment key={venta.id}>
                    <tr 
                      className={`hover:bg-torta-cream/30 cursor-pointer ${venta.anulada ? 'bg-red-50/50' : ''} ${isCatalogOrder ? 'bg-green-50/30' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : venta.id)}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-torta-muted" />
                          <div>
                            <p className="text-sm text-torta-dark">{formatDate(venta.fecha || venta.fecha_creacion)}</p>
                            <p className="text-xs text-torta-muted">
                              {isCatalogOrder ? venta.numero_pedido : venta.id?.slice(0, 8) + "..."}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-torta-muted" />
                          <div>
                            <span className="font-medium text-torta-dark">{venta.vendedor_nombre}</span>
                            {isCatalogOrder && (
                              <p className="text-xs text-green-600 font-medium">Catálogo Digital</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-torta-dark">{venta.cliente_nombre || "-"}</p>
                        {venta.cliente_telefono && (
                          <p className="text-xs text-torta-muted">{venta.cliente_telefono}</p>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-torta-muted" />
                          <span className="text-torta-dark">{unidades} unid.</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {isCatalogOrder ? (
                          // Catalog order status
                          venta.estado === "cancelado" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3" />
                              Cancelado
                            </span>
                          ) : venta.estado === "entregado" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3" />
                              Entregado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              <Package className="w-3 h-3" />
                              {venta.estado || "Pendiente"}
                            </span>
                          )
                        ) : (
                          // Regular venta status
                          venta.anulada ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3" />
                              Anulada
                            </span>
                          ) : venta.editada ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <Edit className="w-3 h-3" />
                              Editada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3" />
                              Activa
                            </span>
                          )
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <span className={`font-display text-lg ${venta.anulada || venta.estado === "cancelado" ? 'text-red-400 line-through' : 'text-torta-brown'}`}>
                          {formatCurrency(venta.total)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          {isCatalogOrder ? (
                            // Catalog orders only show expand
                            <span className="text-xs text-torta-muted">Ver detalles</span>
                          ) : !venta.anulada ? (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={(e) => { e.stopPropagation(); openEditDialog(venta); }}
                                title="Editar venta"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); setAnularDialog({ open: true, venta, motivo: "" }); }}
                                className="text-red-600"
                                title="Anular venta"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleRestaurar(venta); }}
                              className="text-green-600"
                              title="Restaurar venta"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-torta-muted" /> : <ChevronDown className="w-4 h-4 text-torta-muted" />}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="bg-torta-cream/30">
                        <td colSpan={7} className="p-4">
                          <div className="space-y-4">
                            {/* Items */}
                            <div>
                              <h4 className="font-medium text-torta-dark mb-2">Detalle de productos:</h4>
                              <div className="bg-white rounded-lg p-3 space-y-2">
                                {(venta.items || []).map((item, idx) => (
                                  <div key={idx} className="flex justify-between text-sm">
                                    <span>
                                      {item.cantidad}x {item.producto_nombre}
                                    </span>
                                    <span className="text-torta-muted">
                                      @ {formatCurrency(item.precio_unitario)} = {formatCurrency(item.subtotal)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Additional info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-torta-muted">Método de Pago</p>
                                <p className="font-medium capitalize">{venta.metodo_pago_display || venta.metodo_pago}</p>
                              </div>
                              <div>
                                <p className="text-torta-muted">Notas</p>
                                <p className="font-medium">{venta.notas || "-"}</p>
                              </div>
                              {/* Catalog order specific fields */}
                              {venta.origen === "catalogo_digital" && (
                                <>
                                  <div>
                                    <p className="text-torta-muted">Tipo</p>
                                    <p className="font-medium text-green-600">Catálogo Digital</p>
                                  </div>
                                  <div>
                                    <p className="text-torta-muted">Entrega</p>
                                    <p className="font-medium">{venta.tipo_entrega === "domicilio" ? "Domicilio" : "Recoger"}</p>
                                  </div>
                                  {venta.direccion_entrega && (
                                    <div className="col-span-2">
                                      <p className="text-torta-muted">Dirección</p>
                                      <p className="font-medium">{venta.direccion_entrega}</p>
                                      {venta.barrio_entrega && <p className="text-xs text-torta-muted">Barrio: {venta.barrio_entrega}</p>}
                                    </div>
                                  )}
                                </>
                              )}
                              {venta.editada && (
                                <>
                                  <div>
                                    <p className="text-torta-muted">Editada por</p>
                                    <p className="font-medium">{venta.editada_por_nombre}</p>
                                  </div>
                                  <div>
                                    <p className="text-torta-muted">Motivo edición</p>
                                    <p className="font-medium">{venta.notas_edicion || "-"}</p>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Void info */}
                            {venta.anulada && (
                              <div className="bg-red-100 border border-red-200 rounded-lg p-3">
                                <p className="text-red-800 font-medium">Venta Anulada</p>
                                <p className="text-red-600 text-sm">Motivo: {venta.motivo_anulacion}</p>
                                <p className="text-red-500 text-xs mt-1">
                                  Anulada por {venta.anulada_por_nombre} el {formatDate(venta.fecha_anulacion)}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center py-8 text-torta-muted">No se encontraron ventas</p>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Editar Venta</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Info banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm">
                <strong>Nota:</strong> Al editar esta venta, el cálculo de comisiones se actualizará automáticamente.
              </p>
            </div>

            {/* Customer info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre del Cliente</Label>
                <Input
                  value={editForm.cliente_nombre}
                  onChange={(e) => setEditForm({ ...editForm, cliente_nombre: e.target.value })}
                  placeholder="Nombre del cliente"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={editForm.cliente_telefono}
                  onChange={(e) => setEditForm({ ...editForm, cliente_telefono: e.target.value })}
                  placeholder="Teléfono"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <Label>Productos</Label>
              {editForm.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-torta-cream/50 p-3 rounded-lg">
                  <div className="col-span-4">
                    <Input
                      value={item.producto_nombre}
                      onChange={(e) => updateItem(idx, "producto_nombre", e.target.value)}
                      placeholder="Producto"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="1"
                      value={item.cantidad}
                      onChange={(e) => updateItem(idx, "cantidad", parseInt(e.target.value) || 0)}
                      placeholder="Cant"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={item.precio_unitario}
                      onChange={(e) => updateItem(idx, "precio_unitario", parseFloat(e.target.value) || 0)}
                      placeholder="Precio"
                    />
                  </div>
                  <div className="col-span-3 text-right font-medium text-torta-brown">
                    {formatCurrency(item.subtotal)}
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-600">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="text-right font-display text-xl text-torta-brown">
                Total: {formatCurrency(totalEditForm)}
              </div>
            </div>

            {/* Payment method */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <Select
                  value={editForm.metodo_pago}
                  onValueChange={(value) => setEditForm({ ...editForm, metodo_pago: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="nequi">Nequi</SelectItem>
                    <SelectItem value="daviplata">Daviplata</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Input
                  value={editForm.notas}
                  onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })}
                  placeholder="Notas de la venta"
                />
              </div>
            </div>

            {/* Edit reason */}
            <div className="space-y-2">
              <Label>Motivo de la Edición *</Label>
              <Textarea
                value={editForm.notas_edicion}
                onChange={(e) => setEditForm({ ...editForm, notas_edicion: e.target.value })}
                placeholder="Explique el motivo de la edición..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, venta: null })}>
                Cancelar
              </Button>
              <Button onClick={handleEdit} className="bg-torta-brown hover:bg-torta-brown/90">
                Guardar Cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anular Dialog */}
      <Dialog open={anularDialog.open} onOpenChange={(open) => setAnularDialog({ ...anularDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-red-600">Anular Venta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">
                ¿Está seguro de anular esta venta de <strong>{formatCurrency(anularDialog.venta?.total)}</strong>?
              </p>
              <p className="text-sm text-red-600 mt-2">
                • Esta venta <strong>NO</strong> se incluirá en el cálculo de comisiones<br/>
                • La venta quedará marcada como anulada pero no se eliminará<br/>
                • Puede restaurar la venta posteriormente si es necesario
              </p>
            </div>
            <div className="space-y-2">
              <Label>Motivo de Anulación *</Label>
              <Textarea
                required
                value={anularDialog.motivo}
                onChange={(e) => setAnularDialog({ ...anularDialog, motivo: e.target.value })}
                placeholder="Ej: Venta duplicada, error en productos, cliente canceló..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAnularDialog({ open: false, venta: null, motivo: "" })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleAnular}>
                Confirmar Anulación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
